import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ChatbotPreview.css';

// Faithful preview of chatbotStatic/chatbot.js v2: identity header, suggested
// question chips, agent avatar, three-dot typing, powered-by footer — plus a
// "closed" mode showing the launcher with its teaser. Keep this in sync with
// the widget; the preview IS the promise the settings page makes.

function getContrastColor(hexcolor) {
  if (!hexcolor) return 'white';
  if (hexcolor[0] === '#') hexcolor = hexcolor.slice(1);
  if (hexcolor.length === 3) {
    hexcolor = hexcolor[0] + hexcolor[0] + hexcolor[1] + hexcolor[1] + hexcolor[2] + hexcolor[2];
  }
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? 'black' : 'white';
}

const SIMULATED_REPLIES = {
  fr: [
    'Avec plaisir, je regarde ça tout de suite.',
    'Bien sûr ! Voici ce que j’ai trouvé pour vous.',
    'Merci pour votre message, je m’en occupe.',
    'Très bonne question — voici les détails.',
  ],
  en: [
    "I'd be happy to help you with that!",
    'Let me look into that for you.',
    'Sure, I can assist you with this request.',
    "Thanks for reaching out! Here's what I found.",
  ],
};

const STRINGS = {
  fr: { placeholder: 'Écrivez votre message…', poweredBy: 'Propulsé par Unanimity' },
  en: { placeholder: 'Type a message…', poweredBy: 'Powered by Unanimity' },
};

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
    <path d="M3 11.5 21 3l-8.5 18-2.4-7.1z" /><path d="M21 3 10.1 13.9" />
  </svg>
);

const CloseIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: size, height: size }}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

const ChatIconDefault = () => (
  <svg viewBox="0 0 24 24" style={{ width: '55%', height: '55%', fill: 'currentColor' }}>
    <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-3 12H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1zm0-3H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1zm0-3H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1z" />
  </svg>
);

const shadeColor = (hex, factor) => {
  let h = hex && hex[0] === '#' ? hex.slice(1) : (hex || '1F2937');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const f = (i) => Math.max(0, Math.min(255, Math.round(parseInt(h.substr(i, 2), 16) * factor)));
  return `#${[f(0), f(2), f(4)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 17, height: 17 }}>
    <path d="M15 18 9 12l6-6" />
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
    <path d="m9 6 6 6-6 6" />
  </svg>
);

const ChatbotPreview = ({
  primary_color = '#1F2937',
  secondary_color = '#FFFFFF',
  header_color = null,
  company_name = 'Company Name',
  agent_name = 'Bot',
  greetings_message = 'Bonjour ! Comment puis-je vous aider ?',
  subtitle = '',
  logo_url = null,
  suggested_questions = [],
  teaser_enabled = false,
  teaser_message = '',
  powered_by = true,
  language = 'fr',
  bubble_shape = 'circle',
  bubble_size = 60,
  bubble_position = 'bottom_right',
  bubble_border_color,
  bubble_shadow = 'default',
  welcome_title = '',
  mode = 'desktop',
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const replyIndexRef = useRef(0);

  const lang = language === 'en' ? 'en' : 'fr';
  const strings = STRINGS[lang];
  const primaryTextColor = getContrastColor(primary_color);
  const secondaryTextColor = getContrastColor(secondary_color);
  const headerColor = header_color || primary_color;
  const headerTextColor = getContrastColor(headerColor);
  const questions = (suggested_questions || []).filter(Boolean).slice(0, 4);

  useEffect(() => {
    setMessages([]);
    replyIndexRef.current = 0;
    setIsTyping(false);
  }, [primary_color, secondary_color, header_color, company_name, agent_name, greetings_message, language]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isTyping]);

  const sendText = useCallback((text) => {
    const clean = (text || '').trim();
    if (!clean) return;
    setMessages((prev) => [...prev, { id: Date.now(), type: 'user', content: clean }]);
    setInputValue('');
    setIsTyping(true);
    const delay = 900 + Math.random() * 900;
    setTimeout(() => {
      setIsTyping(false);
      const replies = SIMULATED_REPLIES[lang];
      const reply = replies[replyIndexRef.current % replies.length];
      replyIndexRef.current++;
      setMessages((prev) => [...prev, { id: Date.now() + 1, type: 'bot', content: reply }]);
    }, delay);
  }, [lang]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendText(inputValue);
    }
  };

  const bubbleBorderRadius = bubble_shape === 'circle' ? '50%' : '16px';
  const bubbleShadowValue =
    bubble_shadow === 'none' ? 'none'
      : bubble_shadow === 'large' ? '0 8px 25px rgba(0,0,0,0.3)'
        : '0 5px 15px rgba(0,0,0,0.1)';

  const Avatar = ({ size = 22, fontSize = 10 }) => (
    <span
      className="cbp-avatar"
      style={{ width: size, height: size, backgroundColor: primary_color, color: primaryTextColor, fontSize }}
    >
      {logo_url ? <img src={logo_url} alt="" /> : (agent_name || '?').charAt(0).toUpperCase()}
    </span>
  );

  if (mode === 'home') {
    const brandDeep = shadeColor(primary_color, 0.55);
    const title = (welcome_title || '').trim()
      || (lang === 'en' ? 'Hello,\nhow can we help you?' : 'Bonjour,\ncomment pouvons-nous vous aider ?');
    const statusLine = `${agent_name} · ${subtitle || (lang === 'en' ? 'replies in seconds' : 'répond en quelques secondes')}`;
    return (
      <div className="cbp-stage">
        <div className="cbp-widget cbp-home">
          <div className="cbp-home-hero" style={{ background: `linear-gradient(165deg, ${brandDeep} 0%, ${primary_color} 78%)`, color: primaryTextColor }}>
            <div className="cbp-home-top">
              <span className="cbp-home-logo" style={{ color: primary_color }}>
                {logo_url ? <img src={logo_url} alt="" /> : (company_name || '?').charAt(0).toUpperCase()}
              </span>
              <CloseIcon size={17} />
            </div>
            <h2>{title}</h2>
            <p className="cbp-home-status"><span className="cbp-status-dot" />{statusLine}</p>
          </div>
          <div className="cbp-home-cards">
            <div className="cbp-card">
              <span className="cbp-card-av" style={{ backgroundColor: primary_color, color: primaryTextColor }}>
                {logo_url ? <img src={logo_url} alt="" /> : (agent_name || '?').charAt(0).toUpperCase()}
              </span>
              <span className="cbp-card-body">
                <b>{lang === 'en' ? 'Send us a message' : 'Envoyer un message'}</b>
                <span>{statusLine}</span>
              </span>
              <span className="cbp-card-go" style={{ color: primary_color }}><SendIcon /></span>
            </div>
            {questions.map((question, index) => (
              <div className="cbp-card" key={`${question}-${index}`}>
                <span className="cbp-card-body"><b style={{ fontWeight: 550 }}>{question}</b></span>
                <span className="cbp-card-go" style={{ color: primary_color }}><ChevronIcon /></span>
              </div>
            ))}
          </div>
          {powered_by && <div className="cbp-footer" style={{ background: 'transparent' }}>{strings.poweredBy}</div>}
          <div className="cbp-tabbar">
            <span className="cbp-tab active" style={{ color: primary_color }}>{lang === 'en' ? 'Home' : 'Accueil'}</span>
            <span className="cbp-tab">Messages</span>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'closed') {
    const launcherAlign = bubble_position === 'bottom_left' ? 'flex-start' : 'flex-end';
    return (
      <div className="cbp-stage cbp-stage-closed" style={{ alignItems: launcherAlign }}>
        {teaser_enabled && (teaser_message || '').trim() && (
          <div className="cbp-teaser" style={{ alignSelf: launcherAlign }}>
            <span className="cbp-teaser-close"><CloseIcon size={9} /></span>
            {teaser_message}
          </div>
        )}
        <div
          className="cbp-launcher"
          style={{
            width: bubble_size,
            height: bubble_size,
            backgroundColor: primary_color,
            color: primaryTextColor,
            borderRadius: bubbleBorderRadius,
            boxShadow: bubbleShadowValue,
            border: bubble_border_color ? `2px solid ${bubble_border_color}` : 'none',
            alignSelf: launcherAlign,
          }}
        >
          {logo_url ? <img src={logo_url} alt="" className="cbp-launcher-logo" /> : <ChatIconDefault />}
        </div>
      </div>
    );
  }

  return (
    <div className="cbp-stage">
      <div className={`cbp-widget${mode === 'mobile' ? ' mobile' : ''}`}>
        <div className="cbp-header cbp-conv-header">
          <span className="cbp-back"><BackIcon /></span>
          <span className="cbp-header-logo" style={{ backgroundColor: primary_color, color: primaryTextColor }}>
            {logo_url ? <img src={logo_url} alt="" /> : (agent_name || '?').charAt(0).toUpperCase()}
          </span>
          <div className="cbp-header-id">
            <b>{agent_name}</b>
            <span className="cbp-header-sub">
              <span className="cbp-status-dot" />
              {company_name}
            </span>
          </div>
          <span className="cbp-header-close"><CloseIcon /></span>
        </div>

        <div className="cbp-messages">
          <div className="cbp-row">
            <Avatar />
            <div
              className="cbp-msg bot"
              style={{ backgroundColor: secondary_color, color: secondaryTextColor }}
            >
              {greetings_message}
            </div>
          </div>
          {messages.length === 0 && questions.length > 0 && (
            <div className="cbp-chips">
              {questions.map((question, index) => (
                <button
                  key={`${question}-${index}`}
                  type="button"
                  className="cbp-chip"
                  style={{ borderColor: primary_color, color: primary_color }}
                  onClick={() => sendText(question)}
                >
                  {question}
                </button>
              ))}
            </div>
          )}
          {messages.map((message) => (
            message.type === 'bot' ? (
              <div className="cbp-row" key={message.id}>
                <Avatar />
                <div className="cbp-msg bot" style={{ backgroundColor: secondary_color, color: secondaryTextColor }}>
                  {message.content}
                </div>
              </div>
            ) : (
              <div
                className="cbp-msg user"
                key={message.id}
                style={{ backgroundColor: primary_color, color: primaryTextColor }}
              >
                {message.content}
              </div>
            )
          ))}
          {isTyping && (
            <div className="cbp-row">
              <Avatar />
              <span className="cbp-typing" style={{ backgroundColor: secondary_color }}>
                <i style={{ backgroundColor: secondaryTextColor }} />
                <i style={{ backgroundColor: secondaryTextColor }} />
                <i style={{ backgroundColor: secondaryTextColor }} />
              </span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="cbp-input">
          <textarea
            rows={1}
            value={inputValue}
            placeholder={strings.placeholder}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ '--cbp-focus': primary_color }}
          />
          <button
            type="button"
            className="cbp-send"
            style={{ backgroundColor: primary_color, color: primaryTextColor }}
            onClick={() => sendText(inputValue)}
            aria-label="send"
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatbotPreview;
